import { Component } from 'react'
import styled from 'styled-components'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import i18n from '../../i18n'

export default class CalendarView extends Component {
  constructor (props) {
    super(props)
    this.state = {
      currentMonth: new Date(props.selectedDate || new Date())
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.selectedDate !== this.props.selectedDate && this.props.selectedDate) {
      this.setState({ currentMonth: new Date(this.props.selectedDate) })
    }
  }

  getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    return { daysInMonth, startingDayOfWeek, year, month }
  }

  isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear()
  }

  isToday = (date) => {
    return this.isSameDay(date, new Date())
  }

  hasEvents = (date) => {
    const { events } = this.props
    if (!events || events.length === 0) return false

    return events.some(event => {
      // Ignora eventi completati
      if (event.isCompleted) return false

      // Supporta sia formato vecchio (start.dateTime) che nuovo (startDate)
      const startDate = new Date(event.startDate || event.start?.dateTime || event.start?.date)
      const endDate = new Date(event.endDate || event.end?.dateTime || event.end?.date)

      // Normalizza le date a mezzanotte per confronto corretto
      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const eventStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
      const eventEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())

      // Verifica se la data è tra inizio e fine evento (inclusivo)
      return checkDate >= eventStart && checkDate <= eventEnd
    })
  }

  handlePrevMonth = () => {
    this.setState(prev => {
      const newDate = new Date(prev.currentMonth)
      newDate.setMonth(newDate.getMonth() - 1)
      return { currentMonth: newDate }
    })
  }

  handleNextMonth = () => {
    this.setState(prev => {
      const newDate = new Date(prev.currentMonth)
      newDate.setMonth(newDate.getMonth() + 1)
      return { currentMonth: newDate }
    })
  }

  handleDateClick = (day) => {
    const { currentMonth } = this.state
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    if (this.props.onDateSelect) {
      this.props.onDateSelect(selectedDate)
    }
  }

  render () {
    const { selectedDate } = this.props
    const { currentMonth } = this.state
    const { daysInMonth, startingDayOfWeek, year, month } = this.getDaysInMonth(currentMonth)

    const localeMap = { it: 'it-IT', en: 'en-GB', fr: 'fr-FR' }
    const locale = localeMap[i18n.language] || 'it-IT'
    const monthName = currentMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' })

    // Genera array di giorni (con padding iniziale)
    const days = []

    // Padding per i giorni vuoti all'inizio
    // In Italia la settimana inizia da Lunedì (1), ma Date.getDay() restituisce 0 per Domenica
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(null)
    }

    // Giorni del mese
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    return (
      <CalendarContainer>
        <CalendarHeader>
          <MonthNavButton onClick={this.handlePrevMonth} title={i18n.t('calendar.prevMonth', { ns: 'common' })}>
            <FiChevronLeft />
          </MonthNavButton>
          <MonthTitle>{monthName}</MonthTitle>
          <MonthNavButton onClick={this.handleNextMonth} title={i18n.t('calendar.nextMonth', { ns: 'common' })}>
            <FiChevronRight />
          </MonthNavButton>
        </CalendarHeader>

        <WeekDaysHeader>
          {i18n.t('calendar.weekdays', { ns: 'common' }).split(',').map(day => (
            <WeekDay key={day}>{day}</WeekDay>
          ))}
        </WeekDaysHeader>

        <DaysGrid>
          {days.map((day, index) => {
            if (day === null) {
              return <EmptyDay key={`empty-${index}`} />
            }

            const date = new Date(year, month, day)
            const isSelected = selectedDate && this.isSameDay(date, selectedDate)
            const isCurrentDay = this.isToday(date)
            const hasEvents = this.hasEvents(date)

            return (
              <DayCell
                key={day}
                onClick={() => this.handleDateClick(day)}
                $isSelected={isSelected}
                $isToday={isCurrentDay}
                $hasEvents={hasEvents}
              >
                <DayNumber>{day}</DayNumber>
                {hasEvents && <EventDot />}
              </DayCell>
            )
          })}
        </DaysGrid>
      </CalendarContainer>
    )
  }
}

// ============ STYLED COMPONENTS ============

const CalendarContainer = styled.div`
  background: ${props => props.theme.colors.background.hover};
  border-radius: ${props => props.theme.radius.input};
  padding: 1rem;
`

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const MonthTitle = styled.div`
  font-size: 0.938rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  text-transform: capitalize;
  flex: 1;
  text-align: center;
`

const MonthNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.button};
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 0.875rem;
  }

  &:hover {
    background: ${props => props.theme.colors.background.secondary};
    border-color: ${props => props.theme.colors.border.strong};
    color: ${props => props.theme.colors.text.primary};
  }

  &:active {
    transform: scale(0.95);
  }
`

const WeekDaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`

const WeekDay = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.tertiary};
  padding: 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
`

const EmptyDay = styled.div`
  aspect-ratio: 1;
`

const DayCell = styled.button`
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.$isSelected) return props.theme.colors.semantic.primary
    if (props.$isToday) return props.theme.colors.background.secondary
    return 'transparent'
  }};
  border: 1px solid ${props => {
    if (props.$isSelected) return props.theme.colors.semantic.primary
    if (props.$isToday) return props.theme.colors.semantic.primary
    return 'transparent'
  }};
  border-radius: ${props => props.theme.radius.button};
  color: ${props => {
    if (props.$isSelected) return props.theme.colors.text.inverse
    return props.theme.colors.text.primary
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  font-size: 0.813rem;
  font-weight: ${props => props.$isToday || props.$isSelected ? '600' : '500'};

  &:hover:not(:disabled) {
    background: ${props => {
    if (props.$isSelected) return props.theme.colors.semantic.primary
    return props.theme.colors.background.secondary
  }};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`

const DayNumber = styled.span`
  /* Numero del giorno */
`

const EventDot = styled.div`
  position: absolute;
  bottom: 4px;
  width: 5px;
  height: 5px;
  background: ${props => props.theme.colors.semantic.success};
  border-radius: 50%;
`
